import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useForgotPasswordMutation } from "@/api/auth/useAuthAPI";
import LoginPageLayout from "@/components/layout/login";
import ForgotPasswordForm from "@/components/login/ForgotPasswordForm";
import LoginForm from "@/components/login/LoginForm";
import LoadingPage from "@/components/ui/LoadingPage";
import { useAuth } from "@/hooks/auth/useAuth";
import { useSsoAutoStart } from "@/hooks/auth/useSsoAutoStart";
import { LoginPageType } from "@/types/auth";
import { isGlobalLogoutPending } from "@/utils/sso";

const LoginPage = (): JSX.Element => {
  const { isAuthenticating, login, isAuthenticated, isLoggingIn } = useAuth();

  const navigate = useNavigate();

  const [mutateCount, setMutateCount] = useState(0);
  const {
    mutate: forgotPassword,
    status: forgotPasswordStatus,
    reset: resetForgotPassword
  } = useForgotPasswordMutation({
    onSuccess: () => {
      setMutateCount((prev) => prev + 1);
    },
    onError: () => {
      setMutateCount((prev) => prev + 1);
    }
  });

  // Can be changed whether want to use content of login / forgot password page
  const [pageType, setPageType] = useState<LoginPageType>("login");

  // With LOKASI SSO on, /login itself hands off to the IdP, so this form is not
  // normally reachable. It stays reachable as the password fallback
  const { isRedirecting } = useSsoAutoStart(
    !isAuthenticated && !isAuthenticating && !isLoggingIn
  );

  // Effect to redirect to previous path (or root) if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [navigate, isAuthenticated]);

  // A pending global logout also leaves `isRedirecting` false, but the browser
  // is already headed to the IdP logout page — rendering the form would flash it
  // mid hand-off.
  if (
    isAuthenticated ||
    isAuthenticating ||
    isLoggingIn ||
    isRedirecting ||
    isGlobalLogoutPending()
  ) {
    return <LoadingPage />;
  }

  // Render the login page when user is not authenticated
  return (
    <LoginPageLayout>
      {pageType === "login" && (
        <LoginForm
          setPageType={setPageType}
          isLoggingIn={isLoggingIn}
          onSubmit={login}
        />
      )}
      {pageType === "resetPassword" && (
        <ForgotPasswordForm
          setPageType={setPageType}
          forgotPasswordStatus={forgotPasswordStatus}
          mutateCount={mutateCount}
          onSubmit={forgotPassword}
          onReset={() => {
            setMutateCount(0);
            resetForgotPassword();
          }}
        />
      )}
    </LoginPageLayout>
  );
};

export default LoginPage;
